import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from '../components/Button';
import Footer from '../components/Footer';
import { apiService } from '../services/api';
import { PricingPlan } from '../types';
import {
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  ArrowRightIcon,
  CheckIcon,
  SparklesIcon,
  BookOpenIcon,
  DocumentTextIcon,
  BellIcon,
  CurrencyDollarIcon,
  PuzzlePieceIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';

const LandingPage: React.FC = () => {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [pricingPlans, setPricingPlans] = useState<PricingPlan[]>([]);

  useEffect(() => {
    const fetchPricingPlans = async () => {
      try {
        const response = await apiService.getSubscriptionPricingPlans();
        if (response.success && response.data) {
          setPricingPlans(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch pricing plans:', error);
      }
    };

    fetchPricingPlans();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const features = [
    {
      name: 'Automated Order via Chat & Payment Integration',
      description: 'Pemesanan lebih mudah melalui chat dengan konfirmasi otomatis dan link pembayaran langsung. Sistem dapat mengirim detail pemesanan, ID order, Jadwal , serta status pembayaran secara otomatis melalui pesan chat. Terintegrasi dengan payment gateway, proses pembayaran menjadi lebih cepat dan tanpa kerumitan.',
      icon: ChatBubbleLeftRightIcon,
      images: ['/images/feature1.png']
    },
    {
      name: 'Login & User Authentication',
      description: 'Akses aman dan cepat untuk seluruh pengguna. Sistem login dilengkapi autentikasi yang membantu memastikan hanya pengguna terdaftar yang dapat mengakses sistem. Mendukung keamanan data, privasi transaksi, serta pengelolaan akun berbasis peran (role-based access).',
      icon: ChartBarIcon,
      images: ['/images/feature2_1.png', '/images/feature2_2.png']
    },
    {
      name: 'Transaction Management',
      description: 'Kelola riwayat transaksi dengan jelas, lengkap, dan mudah dilacak. Menyediakan data transaksi lengkap, termasuk status pembayaran (lunas, pending, gagal), invoice, nama pelanggan, serta tanggal order . Fitur pencarian dan filter membantu pengelola menganalisa dan menindaklanjuti transaksi secara profesional.',
      icon: ShieldCheckIcon,
      images: ['/images/feature3_1.png']
    },
  ];

  const benefits = [
    {
      name: 'Live Chat Automation',
      description: 'Respond to customers in real time, anytime, anywhere.',
      icon: ChatBubbleLeftRightIcon,
    },
    {
      name: 'Billing Checker',
      description: 'Customers can instantly check invoices, bills, or usage status directly from WhatsApp.',
      icon: DocumentTextIcon,
    },
    {
      name: 'Guided How-To Tutorials',
      description: 'Provide step-by-step instructions and troubleshooting without involving your team.',
      icon: BookOpenIcon,
    },
    {
      name: 'AI-Powered Q&A',
      description: 'Get accurate and contextual responses powered by conversational AI optimized for Indonesian language.',
      icon: SparklesIcon,
    },
    {
      name: 'Service Dashboard',
      description: 'Show users their active services, subscription status, or renewal reminders automatically.',
      icon: ChartBarIcon,
    },
    {
      name: 'Network & Status Updates',
      description: 'Send instant notifications on outages, maintenance schedules, or service changes.',
      icon: BellIcon,
    },
    {
      name: 'Admin Fee Transparency',
      description: 'All transaction admin fees are communicated clearly and neatly separated in your Markey dashboard.',
      icon: CurrencyDollarIcon,
    },
    {
      name: 'Third-Party Integration',
      description: 'Connect Markey with your billing system, CRM, marketplace, or internal apps seamlessly.',
      icon: PuzzlePieceIcon,
    }
  ];

  const testimonials = [
    {
      name: '',
      position: 'Traveliv ',
      content: 'CS AI telah mengubah cara kami melayani pelanggan. Respon waktu turun 80% dan kepuasan pelanggan meningkat signifikan.',
      rating: 5,
    },
    {
      name: '',
      position: 'RSV Helmet',
      content: 'Dengan CS AI, kami dapat menangani 10x lebih banyak pertanyaan pelanggan tanpa menambah tim. Solusi yang luar biasa!',
      rating: 5,
    },
    {
      name: '',
      position: 'Tetanggaku',
      content: 'Implementasi CS AI sangat mudah dan dukungan timnya luar biasa. ROI kami tercapai dalam 3 bulan pertama.',
      rating: 5,
    },
  ];

  const faqs = [
    {
      question: 'What exactly is Markey?',
      answer: 'Markey is an AI-driven customer service assistant built for businesses in Indonesia. It automates WhatsApp conversations, answers FAQs, processes billing info, and helps your customers 24/7.',
    },
    {
      question: 'Why is Markey focused on WhatsApp?',
      answer: 'Because WhatsApp is the most-used communication channel in Indonesia, and we believe customer service should meet people where they already are.',
    },
    {
      question: 'How many messages can Markey handle?',
      answer: 'Depending on your plan, from 10,000 to over 100,000 AI responses per month. Need more? We scale easily.',
    },
    {
      question: 'Can I personalize the chatbot to match my brand?',
      answer: 'Absolutely. You can adjust tone, wording, and responses directly from the dashboard or let our team help craft your brand voice.',
    },
    {
      question: 'Can customers make payments through Markey?',
      answer: 'Yes. If your business uses a Payment Gateway, we can integrate it so customers can complete transactions directly from WhatsApp.',
    },
    {
      question: 'Does Markey support invoice and billing checking?',
      answer: 'Yes. Markey can pull billing data, usage summaries, or invoice history automatically.',
    },
    {
      question: 'What does "Maximum Active Users" mean?',
      answer: 'It\'s the number of unique users interacting with your WhatsApp chatbot each month.',
    },
    {
      question: 'Is Markey secure?',
      answer: 'Very secure. All data is encrypted, and Enterprise clients can opt for private server hosting.',
    },
    {
      question: 'How long does setup take?',
      answer: 'On average, 1-3 business days depending on complexity. We guide you through the entire onboarding process.',
    },
    {
      question: 'Can Markey integrate with our internal systems?',
      answer: 'Yes. Through API, custom workflows, or manday integration, everything is possible.',
    },
  ];



  const toggleFaq = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center">
                <img src="/images/logo-markey.png" alt="CS AI" className="h-12 w-auto" />
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-jet-black-700 hover:text-primary-600 transition-colors">Fitur</a>
              <a href="#benefits" className="text-jet-black-700 hover:text-primary-600 transition-colors">Manfaat</a>
              <a href="#testimonials" className="text-jet-black-700 hover:text-primary-600 transition-colors">Testimoni</a>
              <a href="#pricing" className="text-jet-black-700 hover:text-primary-600 transition-colors">Harga</a>
              <a href="#faq" className="text-jet-black-700 hover:text-primary-600 transition-colors">FAQ</a>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/login" className="text-jet-black-700 hover:text-primary-600">
                Masuk
              </Link>
              <Link to="/register">
                <Button>Mulai Gratis</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-red-50 to-gren-100 py-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-12 lg:gap-8 items-center">
            <div className="lg:col-span-6">
              <div className="text-center lg:text-left">
                <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
                  <span className="block">Customer Service</span>
                  <span className="block text-primary-600">Berbasis AI Terbaik</span>
                </h1>
                <p className="mt-3 max-w-md mx-auto text-base text-gray-500 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl lg:mx-0">
                  Revolusi layanan pelanggan Anda dengan AI assistant cerdas yang memberikan respons
                  instan dan akurat 24/7. Hemat waktu, kurangi biaya, dan tingkatkan kepuasan pelanggan.
                </p>
                <div className="mt-8 max-w-md mx-auto sm:flex sm:justify-center md:mt-8 lg:justify-start lg:max-w-none">
                  <div className="rounded-md shadow">
                    <Link to="/register">
                      <Button size="lg" className="w-full">
                        Mulai Trial Gratis
                        <ArrowRightIcon className="ml-2 h-5 w-5" />
                      </Button>
                    </Link>
                  </div>
                  <div className="mt-3 rounded-md shadow sm:mt-0 sm:ml-3">
                    <Link to="/login">
                      <Button variant="outline" size="lg" className="w-full">
                        Lihat Demo
                      </Button>
                    </Link>
                  </div>
                </div>
                <div className="mt-8 flex items-center justify-center lg:justify-start space-x-8">
                  <div className="flex items-center">
                    <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                    <span className="text-sm text-gray-600">Setup 5 menit</span>
                  </div>
                  {/* <div className="flex items-center">
                    <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                    <span className="text-sm text-gray-600">Tanpa kartu kredit</span>
                  </div> */}
                  <div className="flex items-center">
                    <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                    <span className="text-sm text-gray-600">Dukungan 24/7</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-12 lg:mt-0 lg:col-span-6">
              <div className="relative">
                <img
                  className="w-full rounded-lg shadow-xl"
                  src="/images/hero-cs-ai.jpg"
                  alt="Customer Service AI"
                />
                <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-lg shadow-lg">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ChatBubbleLeftRightIcon className="h-8 w-8 text-primary-600" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">Respons Rata-rata</p>
                      <p className="text-lg font-bold text-primary-600">{"<"} 2 detik</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div id="benefits" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-primary-600 font-semibold tracking-wide uppercase">Manfaat</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              WHAT MARKEY CAN DO FOR YOUR BUSINESS
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              Inspired by how Indonesian customers communicate, Markey helps your team deliver support that feels human, instant, and reliable.
            </p>
          </div>

          <div className="mt-12">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {benefits.map((benefit) => (
                <div key={benefit.name} className="pt-6">
                  <div className="flow-root bg-gray-50 rounded-lg px-6 pb-8">
                    <div className="-mt-6">
                      <div>
                        <span className="inline-flex items-center justify-center p-3 bg-primary-500 rounded-md shadow-lg">
                          <benefit.icon className="h-6 w-6 text-white" aria-hidden="true" />
                        </span>
                      </div>
                      <h3 className="mt-8 text-lg font-medium text-gray-900 tracking-tight">{benefit.name}</h3>
                      <p className="mt-5 text-base text-gray-500">{benefit.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div id="features" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-primary-600 font-semibold tracking-wide uppercase">Fitur Unggulan</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Teknologi Terdepan untuk Customer Service Anda
            </p>
          </div>

          <div className="mt-12 space-y-12">
            {features.map((feature, index) => (
              <div key={feature.name} className={`lg:grid lg:grid-cols-2 lg:gap-8 lg:items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                <div className={`lg:col-start-1 ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                  <h3 className="text-2xl font-extrabold text-gray-900">{feature.name}</h3>
                  <p className="mt-4 text-lg text-gray-500">
                    {feature.description}
                  </p>
                  {/* <div className="mt-6">
                    <Link to="/register">
                      <Button variant="outline">
                        Pelajari Lebih Lanjut
                        <ArrowRightIcon className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div> */}
                </div>
                <div className={`mt-10 -mx-4 relative lg:mt-0 ${index % 2 === 1 ? 'lg:col-start-1' : 'lg:col-start-2'}`}>
                  {feature.images.length === 1 ? (
                    <img
                      className="relative mx-auto rounded-lg shadow-lg"
                      width={490}
                      src={feature.images[0]}
                      alt={feature.name}
                    />
                  ) : (
                    <div className="grid grid-cols-1 gap-4">
                      {feature.images.map((image, imgIndex) => (
                        <img
                          key={imgIndex}
                          className="relative mx-auto rounded-lg shadow-lg"
                          width={490}
                          src={image}
                          alt={`${feature.name} - Gambar ${imgIndex + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div id="testimonials" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-primary-600 font-semibold tracking-wide uppercase">Testimoni</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Apa Kata Klien Kami
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6">
                <p className="text-gray-600 italic mb-4">"{testimonial.content}"</p>
                <div>
                  <p className="text-sm text-gray-500">{testimonial.position}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div id="pricing" className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-primary-600 font-semibold tracking-wide uppercase">Harga</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Pilih Paket yang Sesuai untuk Bisnis Anda
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              Mulai gratis dan upgrade saat Anda siap untuk scale
            </p>
          </div>

          <div className="mt-12 space-y-4 sm:mt-16 sm:space-y-0 sm:grid sm:grid-cols-4 sm:gap-6 lg:max-w-4xl lg:mx-auto xl:max-w-none xl:mx-0 xl:grid-cols-4">
            {pricingPlans.map((plan) => (
              <div
                key={plan.id || plan.name}
                className={`relative rounded-lg border border-gray-200 bg-white p-8 ${plan.recommended ? 'ring-2 ring-primary-500' : ''
                  }`}
              >
                {plan.recommended && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="inline-flex rounded-full bg-primary-500 px-4 py-1 text-sm font-semibold text-white">
                      Paling Populer
                    </span>
                  </div>
                )}
                <h3 className="text-center text-2xl font-semibold text-gray-900">{plan.name}</h3>
                <p className="mt-4 text-center">
                  <span className="text-3xl font-extrabold text-gray-900">{formatCurrency(plan.price)}</span>
                  <span className="text-base text-gray-500">/{plan.billingCycle === 'monthly' ? 'bulan' : 'tahun'}</span>
                </p>
                <p className="mt-4 text-center text-sm text-gray-500 min-h-[40px]">
                  {plan.description}
                </p>
                <ul className="mt-6 space-y-4">
                  {(plan.features || []).map((feature) => (
                    <li key={feature} className="flex">
                      <CheckIcon className="flex-shrink-0 h-6 w-6 text-green-500" />
                      <span className="ml-3 text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <Link to="/register">
                    <Button
                      variant={plan.recommended ? 'primary' : 'outline'}
                      className="w-full"
                    >
                      {plan.price === 0 ? 'Mulai Gratis' : 'Pilih Paket'}
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div id="faq" className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-base text-primary-600 font-semibold tracking-wide uppercase">FAQ</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              FREQUENTLY ASKED QUESTIONS
            </p>
          </div>

          <div className="mt-12">
            <dl className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-6">
                  <dt className="text-lg font-medium text-gray-900">
                    <button
                      className="flex items-center justify-between w-full text-left"
                      onClick={() => toggleFaq(index)}
                    >
                      <span>{faq.question}</span>
                      <ChevronDownIcon
                        className={`h-5 w-5 text-gray-500 transform transition-transform ${expandedFaq === index ? 'rotate-180' : ''
                          }`}
                      />
                    </button>
                  </dt>
                  {expandedFaq === index && (
                    <dd className="mt-2 pr-12">
                      <p className="text-base text-gray-500">{faq.answer}</p>
                    </dd>
                  )}
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-primary-600">
        <div className="max-w-2xl mx-auto text-center py-16 px-4 sm:px-6 sm:py-24 lg:px-8">
          <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
            <span className="block">Siap Meningkatkan Customer Service Anda?</span>
          </h2>
          <p className="mt-4 text-xl leading-6 text-primary-100">
            Mulai trial gratis 7 hari.
          </p>
          <div className="mt-8 flex justify-center">
            <div className="inline-flex rounded-md shadow">
              <Link to="/register">
                <Button size="lg" className="bg-white text-primary-600 hover:bg-gray-50">
                  Mulai Trial Gratis
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LandingPage;